/* @moldura padrao */
/**
 * dashboard.tsx (custos-pedido) — Visão de análise de UM pedido (read-only).
 *
 * Cards (receita/custo/margem) + donut de composição do custo por grupo + ficha
 * do pedido + tabela dos custos. Botão "Editar custos" leva ao formulário.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Modal } from 'react-bootstrap';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import type { PedidoDetalhe } from './shared';
import { badgeSituacao, corGrupo, fmt, rotuloSituacao } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

function Donut({ dados }: { dados: { grupo: string; valor: number; pct: number }[] }) {
  const r = 54;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 140 140" style={{ width: 160, height: 160 }}>
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        style={{ stroke: 'var(--bs-border-color)' }}
        strokeWidth="16"
      />
      {dados.map((g) => {
        const len = (g.pct / 100) * C;
        const seg = (
          <circle
            key={g.grupo}
            cx="70"
            cy="70"
            r={r}
            fill="none"
            style={{ stroke: corGrupo(g.grupo) }}
            strokeWidth="16"
            strokeDasharray={`${len} ${C - len}`}
            strokeDashoffset={-acc}
            transform="rotate(-90 70 70)"
          />
        );
        acc += len;
        return seg;
      })}
    </svg>
  );
}

function StatCard(props: { label: string; valor: string; icone: string; cor: string }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card adminuiux-card mb-3 mb-lg-4">
        <div className="card-body">
          <div className="row gx-3 align-items-center">
            <div className="col-auto">
              <span className={`avatar avatar-40 rounded-circle bg-opacity-10 ${props.cor}`}>
                <i className={`bi ${props.icone}`}></i>
              </span>
            </div>
            <div className="col">
              <p className="text-secondary small mb-1">{props.label}</p>
              <h5 className="mb-0">{props.valor}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustosPedidoDashboardPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [p, setP] = useState<PedidoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmarRemover, setConfirmarRemover] = useState(false);
  const [removendo, setRemovendo] = useState(false);

  useEffect(() => {
    setCarregando(true);
    api
      .get<PedidoDetalhe>(`/custos-pedido/${id}`)
      .then(setP)
      .catch(() => toast.error('Não foi possível carregar o pedido.'))
      .finally(() => setCarregando(false));
  }, [id]);

  async function remover() {
    setRemovendo(true);
    try {
      await api.delete(`/custos-pedido/${id}`);
      toast.success('Pedido removido.');
      navigate(-1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível remover.');
    } finally {
      setRemovendo(false);
      setConfirmarRemover(false);
    }
  }

  if (carregando) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" /> Carregando…
      </div>
    );
  }
  if (!p) return null;

  return (
    <CustomMolduraPagina
      titulo={`Pedido #${p.nr_pedido}`}
      descricao={
        `${p.loja_nome ? `${p.loja_nome} · ` : ''}${p.cliente ?? 'Sem cliente'}` +
        (p.data ? ` · ${formatarData(p.data)}` : '')
      }
      caminho={[{ rotulo: 'Custos de Pedido', para: '/custos-pedido' }]}
      acoes={
        <>
          <span className={`badge ${badgeSituacao(p.situacao)}`}>
            {rotuloSituacao(p.situacao)}
          </span>
          <button
            className="btn btn-link text-danger"
            onClick={() => setConfirmarRemover(true)}
          >
            Remover
          </button>
          <button
            className="btn btn-theme"
            onClick={() => navigate(`/custos-pedido/${p.id}/editar`)}
          >
            <i className="bi bi-pencil me-1"></i>Editar custos
          </button>
        </>
      }
    >

      {/* Cards */}
      <div className="row">
        <StatCard
          label="Receita"
          valor={p.valor_venda != null ? fmt(p.valor_venda) : '—'}
          icone="bi-graph-up-arrow"
          cor="bg-success text-success"
        />
        <StatCard
          label="Custo total"
          valor={fmt(p.custo_total)}
          icone="bi-cash-stack"
          cor="bg-danger text-danger"
        />
        <StatCard
          label="Margem"
          valor={p.margem != null ? fmt(p.margem) : '—'}
          icone="bi-wallet2"
          cor="bg-primary text-primary"
        />
        <StatCard
          label="Margem %"
          valor={p.margem_pct != null ? `${p.margem_pct.toFixed(0)}%` : '—'}
          icone="bi-percent"
          cor="bg-warning text-warning"
        />
      </div>

      <div className="row">
        {/* Tabela de custos */}
        <div className="col-12 col-lg-8">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>Custos lançados ({p.custos.length})
              </h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                  <thead>
                    <tr className="text-secondary text-uppercase" style={{ fontSize: '0.7rem' }}>
                      <th>Categoria</th>
                      <th>Fornecedor</th>
                      <th>Descrição</th>
                      <th>Nº doc</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.custos.map((c) => (
                      <tr key={c.id}>
                        <td className="text-nowrap">
                          {c.grupo && (
                            <span
                              className="d-inline-block rounded-circle me-2 align-middle"
                              style={{ width: 8, height: 8, backgroundColor: corGrupo(c.grupo) }}
                            />
                          )}
                          {c.categoria_nome ?? '—'}
                        </td>
                        <td className="text-secondary">{c.fornecedor_nome ?? '—'}</td>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: 220 }}
                          title={c.descricao ?? ''}
                        >
                          {c.descricao ?? '—'}
                        </td>
                        <td className="text-secondary text-nowrap">{c.num_doc ?? '—'}</td>
                        <td className="text-end text-nowrap fw-semibold">{fmt(c.valor)}</td>
                      </tr>
                    ))}
                    {p.custos.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-center text-secondary">
                          Nenhum custo lançado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold border-top">
                      <td colSpan={4} className="text-end">Total</td>
                      <td className="text-end">{fmt(p.custo_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Direita: composição + ficha */}
        <div className="col-12 col-lg-4">
          {p.composicao.length > 0 && (
            <div className="card adminuiux-card mb-3 mb-lg-4">
              <div className="card-header bg-transparent">
                <h6 className="mb-0">
                  <i className="bi bi-pie-chart me-2"></i>Composição do custo
                </h6>
              </div>
              <div className="card-body text-center">
                <Donut dados={p.composicao} />
                <div className="mt-3 text-start">
                  {p.composicao.map((g) => (
                    <div key={g.grupo} className="row gx-2 align-items-center small mb-1">
                      <div className="col-auto">
                        <span
                          className="d-inline-block rounded-circle"
                          style={{ width: 10, height: 10, backgroundColor: corGrupo(g.grupo) }}
                        />
                      </div>
                      <div className="col text-secondary text-truncate">{g.grupo}</div>
                      <div className="col-auto">{fmt(g.valor)}</div>
                      <div className="col-auto text-secondary" style={{ width: 42, textAlign: 'right' }}>
                        {g.pct.toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>Dados do pedido
              </h6>
            </div>
            <div className="card-body">
              {[
                ['Cliente', p.cliente],
                ['Canal de venda', p.categoria_venda_nome],
                ['Vendedor', p.vendedor],
                ['Origem', p.origem],
                ['Forma de pagamento', p.forma_pgto],
                ['Desconto', p.desconto != null ? fmt(p.desconto) : null],
              ].map(([rotulo, valor]) => (
                <div key={rotulo as string} className="row gx-3 mb-2">
                  <div className="col text-secondary small">{rotulo}</div>
                  <div className="col-auto small text-end">{(valor as string) ?? '—'}</div>
                </div>
              ))}
              {p.obs && (
                <>
                  <hr className="my-2" />
                  <p className="small text-secondary mb-1">Observações</p>
                  <p className="small mb-0">{p.obs}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal className="casaa-modal" show={confirmarRemover} onHide={() => setConfirmarRemover(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remover pedido</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remover o pedido #{p.nr_pedido} e todos os seus custos? Esta ação não pode ser
          desfeita.
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-link" onClick={() => setConfirmarRemover(false)}>
            Cancelar
          </button>
          <button className="btn btn-danger" onClick={remover} disabled={removendo}>
            {removendo && <span className="spinner-border spinner-border-sm me-1" />}
            Remover
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
